import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IPurposeMissionBeliefs } from '../../../services/IPurposeMissionBeliefs';
import { StrtPriority } from '../../../../shared/models/plan/StrtPriority';
import { IStrategyPermissions } from '../../../../models/IStrategyPermissions';
import { PlanStep } from '../../../services/PlanStep';
import { IDeliverableHeadings } from '../../../services/IDeliverableHeadings';
import { EditOverlayComponent } from '../edit-overlay/edit-overlay.component';
import { PriorityStackSectionPosition } from '../../../services/PriorityStackSectionPosition';
import { PlanStepPage } from '../../../services/PlanStepPage';
import { DeliverableStepComponent } from '../deliverable-step.component';
import { APP_CONFIG, AppConfig } from '../../../../app-config.module';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { StrategiesDataStoreService } from '../../../../shared/services/strategies-data-store.service';
import { PriorityService } from '../../../services/priority.service';
import { ResourceService } from '../../../services/resource.service';

@Component({
  selector: 'app-strategy-map',
  templateUrl: './strategy-map.component.html',
  styleUrls: ['./strategy-map.component.scss']
})
export class StrategyMapComponent extends DeliverableStepComponent implements OnInit {
  @Input() purposeMissionBeliefs: IPurposeMissionBeliefs;
  @Input() permissions: IStrategyPermissions;
  @Input() strategyId: number;
  @Input() headings: IDeliverableHeadings;
  @Output() planCoordinateChange = new EventEmitter<PlanCoordinate>();
  @ViewChild(EditOverlayComponent) editOverlayComp: EditOverlayComponent;
  readonly placeholderColor: string = '#F5F6F9';
  public pageIndex: number;
  priorities: StrtPriority[] = [];
  loaded = false;

  constructor(@Inject(APP_CONFIG) private appConfig: AppConfig,
              private sanitizer: DomSanitizer,
              protected cdr: ChangeDetectorRef,
              private _strtDataStore: StrategiesDataStoreService,
              private _priorityService: PriorityService,
              _resourceService: ResourceService) {
    super(PlanCoordinate.stepPageBy(PlanStepPage.StrategyMap), cdr, _resourceService);
  }

  ngOnInit() {
    this.loaded = false;
    this.pageIndex = 0;
    this._strtDataStore.priorities$.subscribe(this.prioritiesHandler.bind(this));
    this._priorityService.fetchPriorities(this.strategyId);
  }


  prioritiesHandler(priorities: StrtPriority[]) {
    this.priorities = priorities;
    this.pageIndex = 0;
    this.loaded = true;
  }

  get pageCount() {
    let pages = 0;
    if (!this.hasPriorities) {
      return pages;
    }
    const pageFactor = this.priorities.length / this.appConfig.uiRules.deliverables.strategyMap.MAX_COLUMNS_PER_PAGE;
    if (this.priorities.length % 3 === 0) {
      pages = pageFactor + 1;
    } else {
      pages = Math.ceil(pageFactor);
    }
    pages = Math.min(pages, this.appConfig.uiRules.deliverables.strategyMap.MAX_PAGE_COUNT);
    return pages;
  }

  get hasFullPriorityList() {
    return this.priorities.length === this.appConfig.businessRules.MAX_PRIORITY_COUNT;
  }

  get canEdit() {
    return this.permissions && this.permissions.canEditBody;
  }

  get purposeTitle() {
    return 'Vision';
  }

  get isPurposePlaceholder() {
    return !(this.purposeMissionBeliefs.purpose.length > 0);
  }

  get purposeDescription() {
    return this.sanitizer.bypassSecurityTrustHtml(this.purposeMissionBeliefs.purpose);
  }

  get missionTitle() {
    return 'Mission';
  }

  get isMissionPlaceholder() {
    return !(this.purposeMissionBeliefs.mission.length > 0);
  }

  get missionDescription() {
    return this.sanitizer.bypassSecurityTrustHtml(this.purposeMissionBeliefs.mission);
  }

  get beliefsTitle() {
    return 'Beliefs';
  }

  get isBeliefsPlaceholder() {
    return !(this.purposeMissionBeliefs.beliefs.length > 0);
  }

  get beliefsDescription() {
    return this.sanitizer.bypassSecurityTrustHtml(this.purposeMissionBeliefs.beliefs);
  }

  get incrementPageVisible() {
    return this.hasPriorities && (this.pageIndex + 1 < this.pageCount);
  }

  get decrementPageVisible() {
    return this.hasPriorities && this.pageIndex > 0;
  }

  get startingIndex() {
    return Math.max(((this.pageIndex) * this.appConfig.uiRules.deliverables.strategyMap.MAX_COLUMNS_PER_PAGE), 0);
  }

  get pagePriorities(): StrtPriority[] {
    if (this.priorities.length > 0) {
      return this.priorities.slice(this.startingIndex,
        this.startingIndex + this.appConfig.uiRules.deliverables.strategyMap.MAX_COLUMNS_PER_PAGE);
    } else {
      return [];
    }
  }
  //
  get hasPriorities() {
    return this.priorities.length > 0;
  }

  get creatingObjectivesCoordinate() {
    return PlanCoordinate.stepBy(PlanStep.CREATE_OBJECTIVES_AND_CRITICAL_INITIATIVES);
  }

  get identifyOutcomesAndKeyMeasures() {
    return PlanCoordinate.stepBy(PlanStep.IDENTIFY_OUTCOMES_AND_KEY_MEASURES);
  }

  onPlanCoordinateChangeDevelopStrategyMap() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.DEVELOP_STRATEGY_MAP));
  }

  onPlanCoordinateChange(planCoordinate: PlanCoordinate) {
    this.planCoordinateChange.emit(planCoordinate);
  }

  onPlanCoordinateChangePurposeMissionBeliefs() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.SYNTHESIZE_RESULTS));
  }

  incrementPage() {
    if (this.pageIndex < this.pageCount) {
      this.pageIndex++;
    }
  }

  decrementPage() {
    if (this.pageIndex > 0) {
      this.pageIndex--;
    }
  }

  get objectivesDataKey() {
    return 'objectives';
  }

  get objectivesDataValueKey() {
    return 'text';
  }

  get objectivesSectionPosition() {
    return PriorityStackSectionPosition.Top;
  }

  get initiativesDataKey() {
    return 'objectives.initiatives';
  }

  get initiativesDataValueKey() {
    return 'description';
  }

  get initiativesSectionPosition() {
    return PriorityStackSectionPosition.Middle;
  }

  get keyMeasuresDataKey() {
    return 'objectives.initiatives.keyMeasures';
  }

  get keyMeasuresDataValueKey() {
    return 'measure';
  }

  get keyMeasureDedupeKey() {
    return 'keyMeasureId';
  }

  get keyMeasuresSectionPosition() {
    return PriorityStackSectionPosition.Bottom;
  }

}
